/**
 * <kk-collection> — progressive enhancement for sections/kk-main-collection.liquid
 *
 * Without JS the toolbar is a plain GET form and every link reloads the page. With JS:
 * - Changing a filter checkbox, price or sort re-renders the results in place (Section Rendering API)
 *   and updates the URL; open dropdowns stay open while you tick boxes.
 * - Active-filter chips, "Clear all" and pagination links re-render the whole section in place.
 * - "Load more" appends the next page's cards.
 * - Back/forward restore the matching results.
 * - Only one filter dropdown is open at a time; Esc or a click outside closes it.
 */
const CHANGE_DELAY = 350;
const PRICE_DELAY = 700;

class KKCollection extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'number') this.#queue(PRICE_DELAY);
    }, { signal });
    this.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#queue(0);
    }, { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });

    // One dropdown at a time.
    this.addEventListener('toggle', (event) => {
      const opened = event.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
      this.querySelectorAll('details[data-kk-collection-dropdown][open]').forEach((d) => d !== opened && (d.open = false));
    }, { capture: true, signal });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('details[data-kk-collection-dropdown]')) this.#closeDropdowns();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = this.querySelector('details[data-kk-collection-dropdown][open]');
      if (!open) return;
      open.open = false;
      /** @type {HTMLElement | null} */ (open.querySelector('summary'))?.focus();
    }, { signal });

    window.addEventListener('popstate', () => this.#render(window.location.href, { full: true, push: false }), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.requestController?.abort();
    clearTimeout(this.timer);
  }

  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.closest('[data-kk-collection-form]')) return;
    if (target instanceof HTMLInputElement && target.type === 'number') return; // handled by `input`
    if (target instanceof HTMLInputElement && target.name === 'sort_by') {
      // Custom sort dropdown: show the chosen option, close the list, then re-render.
      const label = target.closest('label')?.querySelector('.kk-collection__option-label')?.textContent?.trim();
      const text = this.querySelector('[data-kk-collection-sort-label]');
      if (label && text) text.textContent = label;
      target.closest('details')?.removeAttribute('open');
      this.#queue(0);
      return;
    }
    this.#queue(target instanceof HTMLSelectElement ? 0 : CHANGE_DELAY);
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const link = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-kk-collection-link], .kk-collection__pagination a'));
    if (link) {
      event.preventDefault();
      this.#render(link.href, { full: true });
      if (link.closest('.kk-collection__pagination')) this.scrollIntoView({ block: 'start' });
      return;
    }

    const more = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-kk-collection-more]'));
    if (more) {
      event.preventDefault();
      this.#loadMore(more);
    }
  }

  #queue(delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.#render(this.#formUrl()), delay);
  }

  #formUrl() {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-kk-collection-form]'));
    const params = new URLSearchParams();
    if (form) {
      for (const [key, value] of new FormData(form)) {
        if (String(value).trim() !== '') params.append(key, String(value));
      }
    }
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  async #fetchSection(url) {
    this.requestController?.abort();
    this.requestController = new AbortController();
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', this.sectionId || '');
    const response = await fetch(target, { signal: this.requestController.signal });
    if (!response.ok) throw new Error(String(response.status));
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * @param {string} url - page URL (without section_id)
   * @param {{ full?: boolean, push?: boolean }} options - full: replace the whole section (closes dropdowns)
   */
  async #render(url, { full = false, push = true } = {}) {
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.#fetchSection(url);
      const fresh = doc.querySelector('[data-kk-collection-root]');
      const current = this.querySelector('[data-kk-collection-root]');
      if (!fresh || !current) return;

      if (full) {
        current.replaceWith(document.importNode(fresh, true));
      } else {
        // Keep the form (and any open dropdown) in place; refresh everything that depends on results.
        for (const selector of ['[data-kk-collection-results]', '[data-kk-collection-active]', '[data-kk-collection-count]']) {
          const next = fresh.querySelector(selector);
          const now = current.querySelector(selector);
          if (next && now) now.replaceWith(document.importNode(next, true));
        }
        const freshFilters = fresh.querySelectorAll('details[data-kk-collection-filter]');
        current.querySelectorAll('details[data-kk-collection-filter]').forEach((details, index) => {
          const next = freshFilters[index];
          if (!next) return;
          // Pill label/count always; option counts only for closed dropdowns (don't disturb the open one).
          details.querySelector('summary')?.replaceWith(document.importNode(next.querySelector('summary'), true));
          if (!details.open) {
            details.querySelector('.kk-collection__panel')?.replaceWith(document.importNode(next.querySelector('.kk-collection__panel'), true));
          }
        });
      }

      if (push) window.history.pushState({}, '', url);
    } catch (error) {
      if (error?.name !== 'AbortError') window.location.href = url; // fall back to a normal page load
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {HTMLAnchorElement} button */
  async #loadMore(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.#fetchSection(button.href);
      const grid = this.querySelector('[data-kk-collection-grid]');
      const freshGrid = doc.querySelector('[data-kk-collection-grid]');
      if (grid && freshGrid) {
        grid.append(...[...freshGrid.children].map((node) => document.importNode(node, true)));
      }
      const freshMore = doc.querySelector('.kk-collection__more');
      const more = button.closest('.kk-collection__more');
      if (freshMore && more) more.replaceWith(document.importNode(freshMore, true));
      else more?.remove();
    } catch (error) {
      if (error?.name !== 'AbortError') window.location.href = button.href;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  #closeDropdowns() {
    this.querySelectorAll('details[data-kk-collection-dropdown][open]').forEach((details) => (details.open = false));
  }
}

if (!customElements.get('kk-collection')) {
  customElements.define('kk-collection', KKCollection);
}
